/**
 * VE-29142 — read and write the editor's stored panel layout.
 *
 * Two halves, deliberately asymmetric:
 *
 *  - HYDRATE once per VISIT to the editor, not per document open. The layout
 *    is applied when the editor lands on a document and then left alone, so
 *    moving between the request's files does not rearrange the workspace
 *    underneath the reviewer. See `hydratedThisVisit` for what that fixed.
 *  - PERSIST on every change. Cheap (one small JSON write), and it means the
 *    preference survives a tab close as well as a reload; waiting for an exit
 *    would lose it exactly when the browser is least likely to cooperate.
 *
 * What gets hydrated is not simply what was stored. The window's width band is
 * checked first, and a band change REPLACES the stored request layout with that
 * band's defaults — a remembered layout is a remembered fit, and moving from a monitor
 * to a laptop invalidates the fit without the reviewer having changed their
 * mind. The Documents panel is carried across a band change regardless, since
 * it holds work rather than chrome; only the narrowest band overrides it. The
 * tool rail needs none of that: it is stored per band, so the band the window
 * is in either has the reviewer's choice or gets its default.
 *
 * Three scopes are in play and they do not all move together — per request, per
 * window band, per browser. See the storage module's header for which is which.
 *
 * The visit boundary is `CLOSE_DOCUMENT`, dispatched when the DocumentDetails
 * page unmounts. Every way INTO the editor crosses it: arriving from the folder
 * view unmounts and remounts the page, and a pasted deep link is a fresh
 * document load. An in-editor switch is not a route change at all, which is
 * exactly the case being excluded.
 *
 * The request id comes from casemedia's `editorReturnTarget`, which the route
 * saga AWAITS at editor entry, before dispatching OPEN_DOCUMENT
 * (`documents/sagas/routeSaga.ts` — `yield* resolveEditorReturnTarget(...)`
 * precedes the load). So a single read at OPEN_DOCUMENT_SUCCESS is enough and
 * no watch is needed; both branches of the resolve have finished by then, the
 * explicit-caseId one synchronously and the lookup one because the route saga
 * blocks on it.
 *
 * The one gap, deliberately left: a provisional target can be REFINED later by
 * `refineEditorReturnTarget`, which the resolve forks rather than awaits. If
 * that refinement changes the folderId, this has already hydrated against the
 * provisional one and will persist against the refined one — a preference
 * written to a neighbouring request, once, on a cold deep link. Cheap to
 * notice and not worth a watch.
 */
import { all, put, select, takeLatest } from 'typed-redux-saga/macro';

import { selectEditorReturnTarget } from '@redact-modules/casemedia/selectors';
import { selectOrganizationId } from '@common/user-onboarding/selectors/organization';
import {
  CLOSE_DOCUMENT,
  HYDRATE_PANEL_PREFERENCES,
  OPEN_DOCUMENT_SUCCESS,
  SET_DOCUMENTS_PANEL_OPEN,
  SET_DOCUMENTS_PANEL_PINNED,
  SET_PAGES_PANEL_COLLAPSED,
  SET_TOOL_PANEL_EXPANDED,
} from '../actions';
import {
  selectDocumentsPanelOpen,
  selectDocumentsPanelPinned,
  selectPagesPanelCollapsed,
  selectToolPanelExpanded,
} from '../selectors';
import {
  layoutDefaultsFor,
  layoutTierFor,
  PanelPreferences,
  persistLastWindowWidth,
  persistPanelPreferences,
  persistToolPanelExpanded,
  readLastWindowWidth,
  readPanelPreferences,
  readToolPanelExpanded,
} from '../panelPreferences';

/** The request whose layout is in play, or undefined before it resolves. */
function* currentFolderId() {
  const target = yield* select(selectEditorReturnTarget);
  return target?.folderId as string | undefined;
}

/**
 * Has the window changed size enough to invalidate the stored layout?
 *
 * Compared by BAND, not by pixel. A stored width differing at all would
 * invalidate on every open — no two sessions have byte-identical viewports once
 * a devtools pane or a zoom level is involved — and the question the stored
 * layout can actually get wrong is "does this still fit", which is what the
 * bands answer.
 *
 * Returns the band either way, since the caller needs it to pick the defaults.
 */
function currentLayout() {
  const width = typeof window === 'undefined' ? 0 : window.innerWidth;
  const previous = readLastWindowWidth();
  const tier = layoutTierFor(width);
  // No stored width is a first run, and a first run has no layout to keep.
  const changed = previous === null || layoutTierFor(previous) !== tier;
  if (width > 0) {
    persistLastWindowWidth(width);
  }
  return { tier, changed };
}

/**
 * Has the layout already been applied for this visit to the editor?
 *
 * `OPEN_DOCUMENT_SUCCESS` fires for BOTH entering the editor and switching
 * document inside it, and hydrating on the second is wrong: switching a file is
 * not a request to rearrange the workspace. It produced two complaints that
 * looked unrelated and were the same thing —
 *
 *   - the tool rail resizing because the incoming document had a different
 *     stored width, which resizes the canvas and re-runs fit-width zoom;
 *   - the Documents panel closing on a switch, when the actual cause was a
 *     window resize some time earlier: the band is only re-read on open, so a
 *     resize sat unnoticed until the next switch happened to collect it.
 *
 * Applying once per VISIT fixes both, and costs nothing that was wanted: the
 * stored values are still written per band and per request as they change,
 * so leaving and returning still restores them. Only the moment of application
 * moves.
 *
 * Reset on `CLOSE_DOCUMENT`, which means leaving the editor — a switch
 * deliberately routes through `SUSPEND_ACTIVE_DOCUMENT` instead, precisely so
 * it does not land there, which is what makes this distinguishable at all.
 */
let hydratedThisVisit = false;

function* hydrate() {
  if (hydratedThisVisit) {
    return;
  }
  hydratedThisVisit = true;
  const orgId = yield* select(selectOrganizationId);
  /**
   * A window that has crossed into a different band throws the stored layout
   * away and starts from the defaults for the band it is now in.
   *
   * The reasoning is that a remembered layout is a remembered FIT, and the fit
   * is what a resize invalidates: a reviewer who arranged two panels on a
   * 1920px monitor and reopens the editor on a 1280px laptop has not changed
   * their mind about panels, they have changed screens, and restoring that
   * arrangement gives them a page too narrow to read. Within a band the
   * arrangement still fits, so it is kept.
   *
   * Their Documents panel is carried across regardless — it is the one panel
   * that holds work rather than chrome, and losing your place in a forty-file
   * request because you undocked a laptop is a different order of annoyance.
   * The exception is the narrowest band, which has no room to carry it into and
   * says so by returning an explicit `documentsPanelOpen` (see
   * `layoutDefaultsFor`).
   */
  const { tier, changed: bandChanged } = currentLayout();
  const defaults = layoutDefaultsFor(tier);
  // Stored per BAND, so no band-change case: the band the window is in now
  // either has the reviewer's choice or gets its default.
  const toolPanelExpanded =
    readToolPanelExpanded(orgId, tier) ?? defaults.toolPanelExpanded;

  const folderId = yield* currentFolderId();
  if (!folderId) {
    // No request context — a file opened without a case. The band-keyed tool
    // rail is still ours to restore (it does not depend on the request, and a
    // deep link is exactly how a reviewer arrives without one), but the three
    // request-keyed ones must be left ALONE.
    //
    // Not set to their defaults: `HYDRATE_PANEL_PREFERENCES` assigns all four
    // fields, so writing defaults here would close a Documents panel the
    // reviewer had opened, on every document open. Re-stating what is already
    // in the store keeps this a no-op for them.
    yield* put(
      HYDRATE_PANEL_PREFERENCES({
        documentsPanelOpen:
          bandChanged && defaults.documentsPanelOpen !== undefined
            ? defaults.documentsPanelOpen
            : yield* select(selectDocumentsPanelOpen),
        documentsPanelPinned: yield* select(selectDocumentsPanelPinned),
        pagesPanelCollapsed: bandChanged
          ? defaults.pagesPanelCollapsed
          : yield* select(selectPagesPanelCollapsed),
        toolPanelExpanded,
      })
    );
    return;
  }
  const saved = readPanelPreferences(orgId, folderId);
  yield* put(
    HYDRATE_PANEL_PREFERENCES({
      ...saved,
      // Carried across a band change, unless the band names a value for it.
      documentsPanelOpen:
        bandChanged && defaults.documentsPanelOpen !== undefined
          ? defaults.documentsPanelOpen
          : saved.documentsPanelOpen,
      pagesPanelCollapsed: bandChanged
        ? defaults.pagesPanelCollapsed
        : saved.pagesPanelCollapsed,
      toolPanelExpanded,
    })
  );
}

/**
 * Write the tool rail's width against the band the window is in NOW — the
 * screen the reviewer made the choice on, even if they resized since opening.
 *
 * Separate from `persist` below because it is a different store with a
 * different key — merging it into that function would mean one write path
 * juggling two key-spaces.
 */
function* persistToolPanel() {
  const width = typeof window === 'undefined' ? 0 : window.innerWidth;
  if (width <= 0) {
    return;
  }
  const orgId = yield* select(selectOrganizationId);
  const expanded = yield* select(selectToolPanelExpanded);
  persistToolPanelExpanded(orgId, layoutTierFor(width), expanded);
}

/**
 * Persist only the field the action changed.
 *
 * Each watcher names its own field so the write is a merge over what is
 * stored, never a snapshot of current state — see the note in the body.
 */
function* persist(field: keyof PanelPreferences) {
  const folderId = yield* currentFolderId();
  if (!folderId) {
    return;
  }
  const changed: Partial<PanelPreferences> = {
    documentsPanelOpen: yield* select(selectDocumentsPanelOpen),
    documentsPanelPinned: yield* select(selectDocumentsPanelPinned),
    pagesPanelCollapsed: yield* select(selectPagesPanelCollapsed),
  };
  const only = { [field]: changed[field] } as Partial<PanelPreferences>;
  const orgId = yield* select(selectOrganizationId);
  // Merged over what is STORED, not written wholesale from current state.
  // `AUTO_OPEN_DOCUMENTS_PANEL` deliberately skips this watcher so an
  // editor-initiated open is not recorded as the reviewer's choice — but a
  // wholesale write undid that at the next unrelated panel change, which
  // snapshotted `documentsPanelOpen: true` along with it and rewrote the
  // request's stored "closed" preference for good.
  persistPanelPreferences(orgId, folderId, {
    ...readPanelPreferences(orgId, folderId),
    ...only,
  });
}

export function* panelPreferencesSaga() {
  yield* all([
    takeLatest(OPEN_DOCUMENT_SUCCESS, hydrate),
    // Leaving the editor ends the visit, so the next entry hydrates again.
    // A plain function, not a generator: it yields nothing, and `takeLatest`
    // accepts either.
    takeLatest(CLOSE_DOCUMENT, () => {
      hydratedThisVisit = false;
    }),
    takeLatest(SET_DOCUMENTS_PANEL_OPEN, () => persist('documentsPanelOpen')),
    takeLatest(SET_DOCUMENTS_PANEL_PINNED, () =>
      persist('documentsPanelPinned')
    ),
    takeLatest(SET_PAGES_PANEL_COLLAPSED, () => persist('pagesPanelCollapsed')),
    takeLatest(SET_TOOL_PANEL_EXPANDED, persistToolPanel),
  ]);
}
